import React, { useState, useMemo } from 'react';
import {
  Modal,
  Switch,
  Button,
  Group,
  Stack,
  Box,
  Text,
  Autocomplete,
  Paper,
  useMantineTheme
} from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';

const CustomerSearchDialog = ({ opened, onClose, onCustomerSelect, embedded = false }) => {
  const theme = useMantineTheme();
  const [searchValue, setSearchValue] = useState('');
  const [accountType, setAccountType] = useState(true);
  const [accountPeriod, setAccountPeriod] = useState(true);
  const [region, setRegion] = useState(true);
  const [territory, setTerritory] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Sample customer data matching the screenshot
  const customers = [
    {
      customerNumber: 'CU002',
      customerName: 'A A Munro & Co Ltd',
      accountType: 'AA',
      accountPeriod: '1m Cycle',
      creditLimit: '500.0000',
      currentBalance: '0.00',
      paymentTerms: 'COD',
      region: 'AB4',
      territory: 'Phone'
    },
    {
      customerNumber: 'CU003',
      customerName: 'A B C Services Ltd',
      accountType: 'AA',
      accountPeriod: '1m Cycle',
      creditLimit: '1000.0000',
      currentBalance: '0.00',
      paymentTerms: 'COD',
      region: 'AB4',
      territory: 'Phone'
    },
    {
      customerNumber: 'CU004',
      customerName: 'A C HEATING & PLUMBING LTD',
      accountType: 'AA',
      accountPeriod: '1m Cycle',
      creditLimit: '750.0000',
      currentBalance: '0.00',
      paymentTerms: 'COD',
      region: 'AB4',
      territory: 'Phone'
    },
    {
      customerNumber: 'CU005',
      customerName: 'A R CAMPBELL LTD',
      accountType: 'AA',
      accountPeriod: '1m Cycle',
      creditLimit: '2000.0000',
      currentBalance: '0.00',
      paymentTerms: 'COD',
      region: 'AB4',
      territory: 'Phone'
    },
    {
      customerNumber: 'CU006',
      customerName: 'A R HEATING ENGINEERS LTD',
      accountType: 'AA',  
      accountPeriod: '1m Cycle',
      creditLimit: '1500.0000',
      currentBalance: '0.00',
      paymentTerms: 'COD',
      region: 'AB4',
      territory: 'Phone'
    },
    {
      customerNumber: 'CU007',
      customerName: 'A WALKER & SON',
      accountType: 'AA',
      accountPeriod: '1m Cycle', 
      creditLimit: '500.0000',
      currentBalance: '0.00',
      paymentTerms: 'COD',
      region: 'AB4',
      territory: 'Phone'
    }
  ];

  const autocompleteData = customers.map(c => c.customerName);

  const handleRowClick = (customer) => {
    setSelectedCustomer(customer);
    
    // If embedded mode, immediately notify parent of selection
    if (embedded && onCustomerSelect) {
      onCustomerSelect(customer);
    }
  };

  const handleOkClick = () => {
    if (selectedCustomer && onCustomerSelect) {
      onCustomerSelect(selectedCustomer);
    }
    if (onClose) {
      onClose();
    }
  };

  // Define table columns for MRT
  const columns = useMemo(() => [
    {
      accessorKey: 'customerNumber',
      header: 'Customer Number',
      size: 120,
    },
    {
      accessorKey: 'customerName', 
      header: 'Customer Name',
      size: 200,
    },
    {
      accessorKey: 'accountType',
      header: 'Acc Type', 
      size: 80,
    },
    {
      accessorKey: 'accountPeriod',
      header: 'Acc Period',
      size: 100,
    },
    {
      accessorKey: 'creditLimit',
      header: 'Credit Limit',
      size: 120,
      Cell: ({ cell }) => (
        <Text ta="right" size="xs">{cell.getValue()}</Text>
      ),
    },
    {
      accessorKey: 'currentBalance',
      header: 'Current Balance', 
      size: 120,
      Cell: ({ cell }) => (
        <Text ta="right" size="xs">{cell.getValue()}</Text>
      ),
    },
    {
      accessorKey: 'paymentTerms',
      header: 'Payment Terms',
      size: 120,
    },
    {
      accessorKey: 'region',
      header: 'Region',
      size: 80,
    },
    {
      accessorKey: 'territory', 
      header: 'Territory',
      size: 100,
    },
  ], []);

  const table = useMantineReactTable({
    columns,
    data: customers,
    enableRowSelection: true,
    enableMultiRowSelection: false,
    enableSelectAll: false,
    getRowId: (row) => row.customerNumber,
    onRowSelectionChange: (updater) => {
      const newSelection = typeof updater === 'function' ? updater({}) : updater;
      const selectedIds = Object.keys(newSelection);
      if (selectedIds.length > 0) {
        const customer = customers.find(c => c.customerNumber === selectedIds[0]);
        if (customer) {
          handleRowClick(customer);
        }
      }
    },
    mantineTableProps: {
      highlightOnHover: true,
      withBorder: true,
      striped: true,
      fontSize: 'xs',
      style: { fontSize: '11px' }
    },
    mantineTableHeadProps: {
      style: { fontSize: '11px' }
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        const customer = customers.find(c => c.customerNumber === row.id);
        if (customer) {
          handleRowClick(customer);
        }
      },
      style: {
        cursor: 'pointer',
        backgroundColor: selectedCustomer?.customerNumber === row.id 
          ? theme.colorScheme === 'dark' 
            ? theme.colors.blue[9] 
            : theme.colors.blue[1]
          : undefined,
      },
    }),
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    initialState: {
      density: 'xs',
    },
  });

  // The main content that's shared between modal and embedded modes
  const content = (
    <Stack spacing="xs">
      {/* Search Section */}
      <Box style={{ backgroundColor: '#f8f9fa', padding: '8px', border: '1px solid #dee2e6' }}>
        <Group spacing="md" align="flex-end">
          <Box style={{ flex: 1 }}>
            <Text size="xs" weight={500} mb={4}>Starts With :</Text>
            <Autocomplete
              data={autocompleteData}
              value={searchValue}
              onChange={setSearchValue}
              size="xs"
              styles={{
                input: {
                  fontSize: '12px',
                  height: '24px'
                }
              }}
            />
          </Box>
          
          <Group spacing="lg" align="center">
            <Group spacing="xs" align="center">
              <Text size="xs">Account Type :</Text>
              <Switch
                checked={accountType}
                onChange={(e) => setAccountType(e.currentTarget.checked)}
                size="xs"
              />
              <Text size="xs" color="blue">AA</Text>
            </Group>
            
            <Group spacing="xs" align="center">
              <Text size="xs">Account Period :</Text>
              <Switch
                checked={accountPeriod}
                onChange={(e) => setAccountPeriod(e.currentTarget.checked)}
                size="xs"
              />
              <Text size="xs" color="blue">AA</Text>
            </Group>
            
            <Group spacing="xs" align="center">
              <Text size="xs">Region :</Text>
              <Switch
                checked={region}
                onChange={(e) => setRegion(e.currentTarget.checked)}
                size="xs"
              />
              <Text size="xs" color="blue">AA</Text>
            </Group>
            
            <Group spacing="xs" align="center">
              <Text size="xs">Territory :</Text>
              <Switch
                checked={territory}
                onChange={(e) => setTerritory(e.currentTarget.checked)}
                size="xs"
              />
            </Group>
          </Group>
        </Group>
        
        <Group position="right" mt="xs">
          <Button 
            size="xs" 
            variant="filled"
            styles={{
              root: {
                fontSize: '11px',
                height: '22px',
                paddingLeft: '12px',
                paddingRight: '12px'
              }
            }}
          >
            Find Customers
          </Button>
        </Group>
      </Box>

      {/* Table Section */}
      <Box style={{ height: '300px' }}>
        <MantineReactTable table={table} />
      </Box>

      {/* Bottom Buttons - only show in modal mode */}
      {!embedded && (
        <Group position="right" spacing="xs" mt="xs">
          <Button 
            size="xs" 
            variant="default"
            styles={{
              root: {
                fontSize: '11px',
                height: '24px',
                paddingLeft: '16px',
                paddingRight: '16px'
              }
            }}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button 
            size="xs" 
            variant="filled"
            disabled={!selectedCustomer}
            styles={{
              root: {
                fontSize: '11px',
                height: '24px',
                paddingLeft: '16px',
                paddingRight: '16px'
              }
            }}
            onClick={handleOkClick}
          >
            OK
          </Button>
        </Group>
      )}
    </Stack>
  );

  // If embedded mode, return content wrapped in Paper
  if (embedded) {
    return (
      <Paper shadow="sm" p="md">
        {content}
      </Paper>
    );
  }

  // Otherwise, return the original modal
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Customer Search"
      size="xl"
      centered
      styles={{
        title: {
          fontSize: '14px',
          fontWeight: 'normal'
        },
        header: {
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #dee2e6'
        },
        body: {
          padding: '8px'
        }
      }}
    >
      {content}
    </Modal>
  );
};

export default CustomerSearchDialog;